import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import config from '../config/config';
import { authFetch } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Pagination, { DEFAULT_PAGE_SIZE } from './Pagination';
import { TaintFlowGraph, PatternContextCard } from './TaintFlowGraph';
import './VigilScan.css';

/* ── severity palette ──────────────────────────────────────────────────────── */
const SEV = {
  critical: { color: '#dc2626', bg: 'rgba(220,38,38,.10)', icon: '🔴', order: 0 },
  high:     { color: '#ea580c', bg: 'rgba(234,88,12,.10)',  icon: '🟠', order: 1 },
  medium:   { color: '#ca8a04', bg: 'rgba(202,138,4,.10)',  icon: '🟡', order: 2 },
  low:      { color: '#2563eb', bg: 'rgba(37,99,235,.10)',  icon: '🔵', order: 3 },
  info:     { color: '#6b7280', bg: 'rgba(107,114,128,.10)',icon: '⚪', order: 4 },
};
const SEV_KEYS = ['critical', 'high', 'medium', 'low', 'info'];

const PHASES = ['phaseLoad', 'phaseCompile', 'phaseScan', 'phaseDone'];

const CATEGORIES = ['security', 'quality', 'privacy', 'compliance', 'accessibility', 'resilience'];

/* ── helpers ───────────────────────────────────────────────────────────────── */
function shortenPath(p, max = 40) {
  if (!p || p.length <= max) return p;
  const parts = p.split('/');
  if (parts.length <= 2) return p;
  return '…/' + parts.slice(-2).join('/');
}

function classifyFinding(finding) {
  const rid = (finding.ruleId || '').toLowerCase();
  const securityKeywords = [
    'injection', 'xss', 'csrf', 'sqli', 'auth', 'crypto', 'secret', 'credential',
    'password', 'token', 'session', 'insecure', 'overflow', 'traversal', 'redirect',
    'deserializ', 'random', 'broken', 'missing-auth', 'missing-rate', 'file-disclosure',
    'pii', 'timing-attack', 'null-pointer',
    // Command execution & code injection
    'exec', 'rce', 'command', 'scriptlet', 'code-inject', 'os-cmd',
    // Denial of service
    'redos', 'regex-dos', 'dos', 'amplif',
    // Protocol / data format attacks
    'xxe', 'ssrf', 'open-redirect', 'log-inject', 'header-inject',
    'prototype', 'nosql', 'ldap', 'template-inject', 'ssti',
    // Weak cryptography / protocol
    'weak-cipher', 'weak-hash', 'weak-tls', 'hardcod',
  ];
  const complianceKeywords = ['compliance', 'cra-', 'pci-', 'gdpr'];
  const infraKeywords = ['docker', 'k8s', 'kubernetes', 'terraform', 'helm', 'container', 'resource-limit'];

  if (securityKeywords.some(k => rid.includes(k))) return 'security';
  if (complianceKeywords.some(k => rid.includes(k))) return 'compliance';
  if (infraKeywords.some(k => rid.includes(k))) return 'infrastructure';
  return 'quality';
}

function deriveCategory(ruleId) {
  return classifyFinding({ ruleId });
}

function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  const s = (ms / 1000).toFixed(1);
  return `${s}s`;
}

function buildCsv(findings) {
  const header = 'Severity,Rule,File,Line,Message,Confidence\n';
  const rows = findings.map(f =>
    [f.severity, f.ruleId, f.file, f.startLine, `"${(f.message || '').replace(/"/g, '""')}"`, f.confidence].join(',')
  );
  return header + rows.join('\n');
}

function buildSarif(findings, scanId, scanResult) {
  const SEV_TO_LEVEL = { critical: 'error', high: 'error', medium: 'warning', low: 'note', info: 'none' };
  const rules = [...new Set(findings.map(f => f.ruleId).filter(Boolean))].map(id => ({
    id,
    name: id,
    shortDescription: { text: id },
  }));
  const results = findings.map(f => ({
    ruleId: f.ruleId || 'unknown',
    level: SEV_TO_LEVEL[(f.severity || 'info').toLowerCase()] || 'note',
    message: { text: f.message || '' },
    locations: [{
      physicalLocation: {
        artifactLocation: {
          uri: (f.file || '').replace(/\\/g, '/'),
          uriBaseId: '%SRCROOT%',
        },
        region: {
          startLine: f.startLine || 1,
          startColumn: f.startColumn || 1,
          endLine: f.endLine || f.startLine || 1,
          endColumn: f.endColumn || 1,
        },
      },
    }],
  }));
  return {
    '$schema': 'https://raw.githubusercontent.com/oasis-tcs/sarif-spec/master/Schemata/sarif-schema-2.1.0.json',
    version: '2.1.0',
    runs: [{
      tool: {
        driver: {
          name: 'Vigil',
          informationUri: 'https://github.com/vigil-sast/vigil',
          rules,
        },
      },
      originalUriBaseIds: { '%SRCROOT%': { uri: 'file:///' } },
      results,
    }],
  };
}

function computeRiskScore(counts) {
  const c = counts.critical || 0, h = counts.high || 0, m = counts.medium || 0, l = counts.low || 0;
  const score = Math.min(100, c * 10 + h * 4 + m * 2 + l);
  let grade;
  if (c === 0 && h === 0 && m === 0) grade = 'A';
  else if (c === 0 && h === 0) grade = 'A';
  else if (c === 0 && h <= 2) grade = 'B';
  else if (c === 0 && h <= 8) grade = 'C';
  else if (c <= 3) grade = 'C';
  else if (c <= 8) grade = 'D';
  else grade = 'F';
  return { score, grade };
}

const GRADE_COLORS = { A: '#16a34a', B: '#65a30d', C: '#ca8a04', D: '#ea580c', F: '#dc2626' };

function relativeTime(dateStr) {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ── animated counter ──────────────────────────────────────────────────────── */
function AnimatedCounter({ value, duration = 1200 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef();
  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const step = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(from + (value - from) * eased));
      if (progress < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <span>{display}</span>;
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  Language-grouped pack selector                                            */
/* ═══════════════════════════════════════════════════════════════════════════ */

const LANG_GROUPS = [
  { key: 'java',       label: 'Java / Kotlin',     icon: '☕', prefixes: ['lang-java','lang-kotlin','java-','kotlin-','spring-','hibernate-','jakarta-','struts-'] },
  { key: 'js',         label: 'JavaScript',         icon: '🟨', prefixes: ['lang-js-ts','js-','express-','react-','angular-','vue-','nextjs-','deno-','nodegoat-','juiceshop-'] },
  { key: 'ts',         label: 'TypeScript',         icon: '🔷', prefixes: ['ts-','nestjs-'] },
  { key: 'python',     label: 'Python',             icon: '🐍', prefixes: ['lang-python','python-','django-','flask-','fastapi-','sqlalchemy-','celery-','boto3-'] },
  { key: 'go',         label: 'Go',                 icon: '🐹', prefixes: ['lang-go','go-','gin-'] },
  { key: 'csharp',     label: 'C# / .NET',          icon: '💜', prefixes: ['csharp-','dotnet-','aspnet-','ef-core-','signalr-','xaml-','identity-'] },
  { key: 'php',        label: 'PHP',                icon: '🐘', prefixes: ['php-','laravel-','symfony-','wordpress-','drupal-','codeigniter-'] },
  { key: 'ruby',       label: 'Ruby / Rails',       icon: '💎', prefixes: ['ruby-','rails-','sinatra-','railsgoat-'] },
  { key: 'c',          label: 'C / C++',            icon: '⚙️', prefixes: ['lang-c','c-security','cpp-'] },
  { key: 'iac',        label: 'IaC / Terraform',    icon: '🏗️', prefixes: ['terraform-','docker-iac','docker-','k8s-'] },
  { key: 'cross',      label: 'Cross-language',     icon: '🌐', prefixes: ['secrets','owasp-','cwe-','api-','compliance','code-quality','software-quality','logging-','webgoat-','injection-','crypto-','database-','file-network','framework-','privacy-','resilience','ai-generated','accessibility-','lang-'] },
];

function assignGroup(packId) {
  for (const g of LANG_GROUPS) {
    if (g.prefixes.some(p => packId.startsWith(p) || packId === p.replace(/-$/, ''))) return g.key;
  }
  return 'cross';
}

function LanguageGroupedPacks({ rulePacks, selectedPacks, togglePack, toggleAllPacks }) {
  const [collapsed, setCollapsed] = React.useState({});

  const groups = React.useMemo(() => {
    const map = {};
    for (const g of LANG_GROUPS) map[g.key] = { ...g, packs: [] };
    for (const p of rulePacks) {
      const id = typeof p === 'string' ? p : (p.id || p.name);
      const gKey = assignGroup(id);
      map[gKey].packs.push({ id, label: typeof p === 'string' ? p : (p.name || p.id) });
    }
    return LANG_GROUPS.map(g => map[g.key]).filter(g => g.packs.length > 0);
  }, [rulePacks]);

  const allSelected = selectedPacks.length === rulePacks.length;

  return (
    <div className="vigil-pack-groups">
      {/* Select all */}
      <label className="vigil-pack-check vigil-pack-select-all">
        <input type="checkbox" checked={allSelected} onChange={toggleAllPacks} />
        <span className="ms-2 fw-semibold">All packs ({rulePacks.length})</span>
      </label>

      {/* Language groups */}
      {groups.map(g => {
        const groupSelected = g.packs.filter(p => selectedPacks.includes(p.id)).length;
        const allGroupSelected = groupSelected === g.packs.length;
        const isOpen = !collapsed[g.key];

        const toggleGroup = () => {
          if (allGroupSelected) {
            g.packs.forEach(p => { if (selectedPacks.includes(p.id)) togglePack(p.id); });
          } else {
            g.packs.forEach(p => { if (!selectedPacks.includes(p.id)) togglePack(p.id); });
          }
        };

        return (
          <div key={g.key} className="vigil-pack-group">
            <div className="vigil-pack-group-header" onClick={() => setCollapsed(c => ({ ...c, [g.key]: !c[g.key] }))}>
              <label className="vigil-pack-check" onClick={e => e.stopPropagation()}>
                <input type="checkbox" checked={allGroupSelected} ref={el => { if (el) el.indeterminate = groupSelected > 0 && !allGroupSelected; }}
                  onChange={toggleGroup} />
              </label>
              <span className="vigil-pack-group-icon">{g.icon}</span>
              <span className="vigil-pack-group-label">{g.label}</span>
              <span className="vigil-pack-group-count">{groupSelected}/{g.packs.length}</span>
              <span className="vigil-pack-group-arrow">{isOpen ? '▾' : '▸'}</span>
            </div>
            {isOpen && (
              <div className="vigil-pack-group-items">
                {g.packs.map(p => (
                  <label key={p.id} className="vigil-pack-check vigil-pack-item">
                    <input type="checkbox" checked={selectedPacks.includes(p.id)} onChange={() => togglePack(p.id)} />
                    <span className="ms-2">{p.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════ */
/*  VigilScan — main component                                               */
/* ═══════════════════════════════════════════════════════════════════════════ */
export default function VigilScan() {
  const { t } = useTranslation();

  /* ── state ─────────────────────────────────────────────────────────────── */
  const [mode, setMode] = useState('setup');           // setup | scanning | results
  const [repoPath, setRepoPath] = useState('');
  const [selectedEngine, setSelectedEngine] = useState('opengrep');
  const [rulePacks, setRulePacks] = useState([]);
  const [selectedPacks, setSelectedPacks] = useState([]);
  const [recentScans, setRecentScans] = useState([]);
  const [scanId, setScanId] = useState(null);
  const [phase, setPhase] = useState(0);               // 0..3
  const [statusMsg, setStatusMsg] = useState('');
  const [logs, setLogs] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [scanResult, setScanResult] = useState(null);

  // results filters
  const [sevFilter, setSevFilter] = useState('all');
  const [searchQ, setSearchQ] = useState('');
  const [sortCol, setSortCol] = useState('severity');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [expandedRow, setExpandedRow] = useState(null);

  const [elapsedTime, setElapsedTime] = useState(0);

  const logBoxRef = useRef(null);
  const eventSourceRef = useRef(null);
  const scanStartRef = useRef(null);

  const { token } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const ruleCacheRef = useRef({});
  const investigationCacheRef = useRef({});
  const [investigationResults, setInvestigationResults] = useState({});
  const [investigatingIds, setInvestigatingIds] = useState({});
  const [investigationErrors, setInvestigationErrors] = useState({});
  const [fileFilter, setFileFilter] = useState(null);
  const [expandedDirs, setExpandedDirs] = useState({});
  const [ruleDetails, setRuleDetails] = useState({});
  const [loadingRules, setLoadingRules] = useState({});
  const [copiedPath, setCopiedPath] = useState(null);

  // AI Bulk Triage state
  const [selectedFindings, setSelectedFindings] = useState(new Set());
  const [triageResults, setTriageResults] = useState(null);
  const [triageLoading, setTriageLoading] = useState(false);
  const [triageError, setTriageError] = useState(null);
  const [triageAccepted, setTriageAccepted] = useState({});  // {findingId: 'accepted'|'rejected'}
  const [triageFilter, setTriageFilter] = useState('all');    // all|escalate|alert|suppress|group

  // Scan profiles
  const [scanProfiles, setScanProfiles] = useState([]);
  const [packCategories, setPackCategories] = useState({}); // eslint-disable-line no-unused-vars
  const [activeProfile, setActiveProfile] = useState('full');
  // Category filter in results
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [ruleFilter, setRuleFilter] = useState(null);

  /* ── fetch rule packs + recent scans on mount ──────────────────────────── */
  useEffect(() => {
    axios.get(`${config.BASE_URL}/vigil/rulepacks`).then(r => {
      const packs = r.data || [];
      setRulePacks(packs);
      setSelectedPacks(packs.map(p => (typeof p === 'string' ? p : p.id || p.name)));
    }).catch(() => {});
    fetchRecentScans();
  }, []);

  useEffect(() => {
    axios.get(`${config.BASE_URL}/vigil/scan/profiles`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(r => {
      setScanProfiles(r.data?.profiles || []);
      setPackCategories(r.data?.packCategories || {});
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRecentScans = () => {
    axios.get(`${config.BASE_URL}/vigil/scans`).then(r => {
      setRecentScans((r.data?.scans || []).slice(0, 8));
    }).catch(() => {});
  };

  /* ── auto-scroll log ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
  }, [logs]);

  /* ── clean up SSE on unmount ───────────────────────────────────────────── */
  useEffect(() => {
    return () => { if (eventSourceRef.current) eventSourceRef.current.close?.(); };
  }, []);

  /* ── elapsed timer during scan ─────────────────────────────────────────── */
  useEffect(() => {
    if (mode !== 'scanning') { setElapsedTime(0); return; }
    const interval = setInterval(() => {
      if (scanStartRef.current) {
        setElapsedTime(Math.floor((Date.now() - scanStartRef.current) / 1000));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [mode]);

  /* ── start scan ────────────────────────────────────────────────────────── */
  const startScan = async () => {
    if (!repoPath.trim()) return;
    setMode('scanning');
    setPhase(0);
    setStatusMsg(t('vigilScan.loadingRules'));
    setLogs([]);
    setWarnings([]);
    setScanResult(null);
    scanStartRef.current = Date.now(); setElapsedTime(0);

    try {
      const { data } = await axios.post(`${config.BASE_URL}/vigil/scan`, {
        repoPath: repoPath.trim(),
        packs: selectedPacks,
        engine: selectedEngine,
      });
      const id = data.id;
      setScanId(id);
      connectSSE(id);
    } catch (err) {
      setStatusMsg(t('vigilScan.scanFailed'));
      setLogs(prev => [...prev, { type: 'error', message: err.message }]);
    }
  };

  /* ── SSE connection (uses fetch for auth header) ───────────────────────── */
  const connectSSE = useCallback(async (id) => {
    try {
      const response = await authFetch(`${config.BASE_URL}/vigil/scan/${id}/stream`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      eventSourceRef.current = { close: () => reader.cancel() };
      let buffer = '';

      const read = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';
          for (const line of lines) {
            const trimmed = line.replace(/^data:\s*/, '').trim();
            if (!trimmed) continue;
            try {
              const evt = JSON.parse(trimmed);
              handleSSEEvent(evt, id);
            } catch { /* skip non-JSON lines */ }
          }
        }
      };
      read();
    } catch {
      loadFinalResult(id);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── handle individual SSE event ───────────────────────────────────────── */
  const handleSSEEvent = (evt, id) => {
    const msg = evt.message || '';
    switch (evt.type) {
      case 'status':
        setLogs(p => [...p, { type: 'status', message: msg }]);
        setStatusMsg(msg);
        if (/loaded/i.test(msg)) setPhase(1);
        else if (/compil/i.test(msg)) setPhase(2);
        else if (/scanning/i.test(msg)) setPhase(3);
        break;
      case 'warning':
        setWarnings(p => [...p, msg]);
        setLogs(p => [...p, { type: 'warning', message: msg }]);
        break;
      case 'log':
        setLogs(p => [...p, { type: 'log', message: msg }]);
        break;
      case 'complete':
        setPhase(3);
        setStatusMsg(t('vigilScan.scanComplete'));
        setLogs(p => [...p, { type: 'complete', message: t('vigilScan.scanComplete') }]);
        if (evt.data) {
          loadFinalResult(id);
        }
        break;
      case 'error':
        setStatusMsg(t('vigilScan.scanFailed'));
        setLogs(p => [...p, { type: 'error', message: msg }]);
        break;
      default:
        setLogs(p => [...p, { type: 'log', message: msg }]);
    }
  };

  /* ── load full result from REST ────────────────────────────────────────── */
  const loadFinalResult = async (id) => {
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/scan/${id}`);
      setScanResult(data);
      setMode('results');
      setSearchParams({ id }, { replace: true });
      fetchRecentScans();
    } catch { /* retry later */ }
  };

  /* ── view a previous scan ──────────────────────────────────────────────── */
  const viewScan = async (id) => {
    setScanId(id);
    await loadFinalResult(id);
  };

  /* ── rescan ────────────────────────────────────────────────────────────── */
  const rescan = () => {
    setMode('setup');
    setScanResult(null);
    setScanId(null);
    setPhase(0);
    setLogs([]);
    setWarnings([]);
    setSearchParams({}, { replace: true });
  };

  /* ── rescan from a previous scan path ──────────────────────────────────── */
  const rescanFrom = (scan) => {
    setRepoPath(scan.path || scan.repoPath || '');
  };

  /* ── restore scan from URL ?id= or router location.state on mount ───────── */
  useEffect(() => {
    const idFromParam = searchParams.get('id');
    const state = location.state;
    if (idFromParam) {
      viewScan(idFromParam);
    } else if (state?.scanId) {
      viewScan(state.scanId);
      window.history.replaceState({}, '');
    } else if (state?.repoPath) {
      setRepoPath(state.repoPath);
      if (state.rulePacks) setSelectedPacks(state.rulePacks);
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── fetch rule detail ──────────────────────────────────────────────────── */
  const fetchRuleDetail = useCallback(async (ruleId) => {
    if (ruleCacheRef.current[ruleId]) {
      setRuleDetails(prev => ({ ...prev, [ruleId]: ruleCacheRef.current[ruleId] }));
      return;
    }
    if (loadingRules[ruleId]) return;
    setLoadingRules(prev => ({ ...prev, [ruleId]: true }));
    try {
      const { data } = await axios.get(`${config.BASE_URL}/vigil/rules/${ruleId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      ruleCacheRef.current[ruleId] = data;
      setRuleDetails(prev => ({ ...prev, [ruleId]: data }));
    } catch {
      // rule detail not available
    } finally {
      setLoadingRules(prev => ({ ...prev, [ruleId]: false }));
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── toggle pack selection ─────────────────────────────────────────────── */
  const togglePack = (packId) => {
    setSelectedPacks(prev =>
      prev.includes(packId) ? prev.filter(p => p !== packId) : [...prev, packId]
    );
    setActiveProfile('custom');
  };
  const toggleAllPacks = () => {
    const allIds = rulePacks.map(p => (typeof p === 'string' ? p : p.id || p.name));
    setSelectedPacks(prev => (prev.length === allIds.length ? [] : allIds));
    setActiveProfile('custom');
  };

  const selectProfile = (profile) => {
    setActiveProfile(profile.id);
    if (!profile.packs || profile.packs.length === 0) {
      const allIds = rulePacks.map(p => (typeof p === 'string' ? p : p.id || p.name));
      setSelectedPacks(allIds);
    } else {
      setSelectedPacks([...profile.packs]);
    }
  };

  /* ── CSV export ────────────────────────────────────────────────────────── */
  const exportCsv = () => {
    if (!scanResult?.findings) return;
    const csv = buildCsv(filteredFindings);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vigil-scan-${scanId || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── JSON export ──────────────────────────────────────────────────────── */
  const exportJson = () => {
    if (!scanResult) return;
    const json = JSON.stringify(scanResult, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vigil-scan-${scanId || 'export'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── Filtered JSON export ─────────────────────────────────────────────── */
  const exportFilteredJson = () => {
    const json = JSON.stringify(filteredFindings, null, 2);
    const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vigil-scan-${scanId || 'export'}-filtered.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── SARIF export ─────────────────────────────────────────────────────── */
  const exportSarif = () => {
    const sarif = buildSarif(filteredFindings, scanId, scanResult);
    const blob = new Blob([JSON.stringify(sarif, null, 2)], { type: 'application/json;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vigil-scan-${scanId || 'export'}.sarif.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ── clipboard helper ─────────────────────────────────────────────────── */
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedPath(text);
      setTimeout(() => setCopiedPath(null), 2000);
    }).catch(() => {});
  };

  /* ── finding ID helper ───────────────────────────────────────────────── */
  const getFindingId = (f) => `${f.ruleId}::${f.file}::${f.startLine}`;

  /* ── AI investigate finding ─────────────────────────────────────────── */
  const investigateFinding = async (finding) => {
    const fid = `${finding.ruleId}::${finding.file}::${finding.startLine}`;
    if (investigationCacheRef.current[fid]) {
      setInvestigationResults(prev => ({ ...prev, [fid]: investigationCacheRef.current[fid] }));
      return;
    }
    setInvestigatingIds(prev => ({ ...prev, [fid]: true }));
    setInvestigationErrors(prev => ({ ...prev, [fid]: null }));
    try {
      const { data } = await axios.post(
        `${config.BASE_URL}/vigil/ai/investigate`,
        {
          ruleId: finding.ruleId,
          file: finding.file,
          line: finding.startLine,
          severity: finding.severity,
          confidence: finding.confidence,
          engine: finding.engine,
          message: finding.message,
          snippet: finding.snippet,
          repoPath: scanResult?.path || scanResult?.repoPath || repoPath,
          flowPath: finding.flowPath || [],
          cwe: ruleDetails[finding.ruleId]?.cwe || [],
          owasp: ruleDetails[finding.ruleId]?.owasp || [],
          captures: finding.captures || {},
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      investigationCacheRef.current[fid] = data;
      setInvestigationResults(prev => ({ ...prev, [fid]: data }));
    } catch (err) {
      setInvestigationErrors(prev => ({ ...prev, [fid]: err.message || 'Unknown error' }));
    } finally {
      setInvestigatingIds(prev => ({ ...prev, [fid]: false }));
    }
  };

  /* ── AI Bulk Triage ─────────────────────────────────────────────────── */
  const toggleFindingSelection = (findingId) => {
    setSelectedFindings(prev => {
      const next = new Set(prev);
      if (next.has(findingId)) next.delete(findingId);
      else next.add(findingId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedFindings.size === filteredFindings.length) {
      setSelectedFindings(new Set());
    } else {
      setSelectedFindings(new Set(filteredFindings.map(f => getFindingId(f))));
    }
  };

  const clearTriageState = () => {
    setSelectedFindings(new Set());
    setTriageResults(null);
    setTriageError(null);
    setTriageAccepted({});
    setTriageFilter('all');
  };

  const runBulkTriage = async () => {
    if (selectedFindings.size === 0) return;
    setTriageLoading(true);
    setTriageError(null);
    setTriageResults(null);
    try {
      const selected = findings.filter(f => selectedFindings.has(getFindingId(f)));
      const payload = {
        findings: selected.map((f, i) => ({
          id: `f${i}`,
          ruleId: f.ruleId,
          severity: (f.severity || 'info').toLowerCase(),
          file: f.file,
          startLine: f.startLine,
          message: f.message,
          snippet: f.snippet || '',
          confidence: (f.confidence || '').toLowerCase(),
        })),
        repoPath: scanResult?.path || scanResult?.repoPath || repoPath,
      };
      const tok = token || localStorage.getItem('token');
      const { data } = await axios.post(
        `${config.BASE_URL}/vigil/ai/triage`,
        payload,
        { headers: tok ? { Authorization: `Bearer ${tok}` } : {} }
      );
      // Map API response IDs back to finding IDs
      const decisionMap = {};
      const selected2 = findings.filter(f => selectedFindings.has(getFindingId(f)));
      (data.decisions || []).forEach((d, i) => {
        const origFinding = selected2[i];
        if (origFinding) {
          decisionMap[getFindingId(origFinding)] = d;
        }
      });
      setTriageResults({ decisions: decisionMap, stats: data.stats, provider: data.provider, model: data.model });
    } catch (err) {
      setTriageError(err.response?.data?.error || err.message || 'Triage failed');
    } finally {
      setTriageLoading(false);
    }
  };

  const acceptTriageDecision = (findingId) => {
    setTriageAccepted(prev => ({ ...prev, [findingId]: 'accepted' }));
  };

  const rejectTriageDecision = (findingId) => {
    setTriageAccepted(prev => ({ ...prev, [findingId]: 'rejected' }));
  };

  const acceptAllOfAction = (action) => {
    if (!triageResults?.decisions) return;
    const updates = {};
    Object.entries(triageResults.decisions).forEach(([fid, d]) => {
      if (d.action === action) updates[fid] = 'accepted';
    });
    setTriageAccepted(prev => ({ ...prev, ...updates }));
  };

  const acceptAll = () => {
    if (!triageResults?.decisions) return;
    const updates = {};
    Object.keys(triageResults.decisions).forEach(fid => { updates[fid] = 'accepted'; });
    setTriageAccepted(prev => ({ ...prev, ...updates }));
  };

  const triageAcceptedCount = Object.values(triageAccepted).filter(v => v === 'accepted').length;
  const triagePendingCount = triageResults ? Object.keys(triageResults.decisions).length - triageAcceptedCount - Object.values(triageAccepted).filter(v => v === 'rejected').length : 0;

  /* ── toggle file tree directory ────────────────────────────────────────── */
  const toggleDir = (path) => {
    setExpandedDirs(prev => ({ ...prev, [path]: !prev[path] }));
  };

  /* ── findings data processing ──────────────────────────────────────────── */
  const findings = useMemo(() => {
    const f = scanResult?.findings;
    if (Array.isArray(f)) return f;
    if (typeof f === 'string') { try { const p = JSON.parse(f); return Array.isArray(p) ? p : []; } catch { return []; } }
    return [];
  }, [scanResult]);

  const sevCounts = useMemo(() => {
    const c = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
    findings.forEach(f => { const s = (f.severity || 'info').toLowerCase(); if (c[s] !== undefined) c[s]++; });
    return c;
  }, [findings]);

  const pieData = useMemo(() =>
    SEV_KEYS.map(k => ({ name: k, value: sevCounts[k] })).filter(d => d.value > 0),
    [sevCounts]
  );

  const topRules = useMemo(() => {
    const m = {};
    findings.forEach(f => { m[f.ruleId] = (m[f.ruleId] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 10)
      .map(([name, count]) => ({ name, count }));
  }, [findings]);

  const topFiles = useMemo(() => {
    const m = {};
    findings.forEach(f => { m[f.file] = (m[f.file] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 10)
      .map(([name, count]) => ({ name: shortenPath(name), fullPath: name, count }));
  }, [findings]);

  const sevByCategory = useMemo(() => {
    const m = {};
    CATEGORIES.forEach(cat => { m[cat] = { category: cat, critical: 0, high: 0, medium: 0, low: 0, info: 0 }; });
    findings.forEach(f => {
      const cat = deriveCategory(f.ruleId);
      const sev = (f.severity || 'info').toLowerCase();
      if (m[cat] && m[cat][sev] !== undefined) m[cat][sev]++;
    });
    return Object.values(m).filter(row => SEV_KEYS.some(k => row[k] > 0));
  }, [findings]);

  /* ── file tree data ───────────────────────────────────────────────────── */
  const fileTree = useMemo(() => {
    const tree = {};
    findings.forEach(f => {
      const filePath = f.file || '';
      const parts = filePath.split('/').filter(Boolean);
      let current = tree;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isFile = i === parts.length - 1;
        if (!current[part]) {
          current[part] = isFile ? { __file: true, __path: filePath, __count: 0, __maxSev: 'info' } : { __dir: true };
        }
        if (isFile) {
          current[part].__count++;
          const sev = (f.severity || 'info').toLowerCase();
          if ((SEV[sev]?.order ?? 5) < (SEV[current[part].__maxSev]?.order ?? 5)) {
            current[part].__maxSev = sev;
          }
        }
        if (!isFile) current = current[part];
      }
    });
    return tree;
  }, [findings]);

  const topAffectedFiles = useMemo(() => {
    const m = {};
    const sevMap = {};
    findings.forEach(f => {
      const fp = f.file || '';
      m[fp] = (m[fp] || 0) + 1;
      const sev = (f.severity || 'info').toLowerCase();
      if (!sevMap[fp]) sevMap[fp] = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
      if (sevMap[fp][sev] !== undefined) sevMap[fp][sev]++;
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).slice(0, 5)
      .map(([path, count]) => ({ path, name: shortenPath(path, 30), count, sevs: sevMap[path] }));
  }, [findings]);

  const ruleCoverage = useMemo(() => {
    const packsWithFindings = new Set();
    findings.forEach(f => {
      const ruleId = f.ruleId || '';
      const parts = ruleId.split(/[-_.]/);
      if (parts.length > 1) packsWithFindings.add(parts[0]);
    });
    const totalPacks = (scanResult?.rulePacks || selectedPacks || []).length;
    return { active: packsWithFindings.size, total: totalPacks || packsWithFindings.size };
  }, [findings, scanResult, selectedPacks]);

  const riskInfo = useMemo(() => computeRiskScore(sevCounts), [sevCounts]);

  /* ── filtered + sorted findings ────────────────────────────────────────── */
  const filteredFindings = useMemo(() => {
    let list = findings;
    if (fileFilter) list = list.filter(f => f.file === fileFilter);
    if (sevFilter !== 'all') list = list.filter(f => (f.severity || '').toLowerCase() === sevFilter);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      list = list.filter(f =>
        (f.ruleId || '').toLowerCase().includes(q) ||
        (f.file || '').toLowerCase().includes(q) ||
        (f.message || '').toLowerCase().includes(q)
      );
    }
    if (triageFilter !== 'all' && triageResults?.decisions) {
      list = list.filter(f => {
        const d = triageResults.decisions[getFindingId(f)];
        return d && d.action === triageFilter;
      });
    }
    if (categoryFilter !== 'all') {
      list = list.filter(f => classifyFinding(f) === categoryFilter);
    }
    if (ruleFilter) list = list.filter(f => f.ruleId === ruleFilter);
    list = [...list].sort((a, b) => {
      let va, vb;
      if (sortCol === 'severity') {
        va = SEV[(a.severity || 'info').toLowerCase()]?.order ?? 5;
        vb = SEV[(b.severity || 'info').toLowerCase()]?.order ?? 5;
      } else if (sortCol === 'line') {
        va = a.startLine || 0; vb = b.startLine || 0;
      } else {
        va = (a[sortCol] || '').toLowerCase();
        vb = (b[sortCol] || '').toLowerCase();
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [findings, fileFilter, sevFilter, searchQ, sortCol, sortDir, triageFilter, triageResults, categoryFilter, ruleFilter]);

  const categoryCounts = useMemo(() => {
    let base = findings;
    if (fileFilter) base = base.filter(f => f.file === fileFilter);
    if (sevFilter !== 'all') base = base.filter(f => (f.severity || '').toLowerCase() === sevFilter);
    if (searchQ) {
      const q = searchQ.toLowerCase();
      base = base.filter(f =>
        (f.ruleId || '').toLowerCase().includes(q) ||
        (f.file || '').toLowerCase().includes(q) ||
        (f.message || '').toLowerCase().includes(q)
      );
    }
    if (triageFilter !== 'all' && triageResults?.decisions) {
      base = base.filter(f => {
        const fid = getFindingId(f);
        const d = triageResults.decisions[fid];
        return d && d.action === triageFilter;
      });
    }
    if (ruleFilter) base = base.filter(f => f.ruleId === ruleFilter);
    const counts = { all: base.length, security: 0, quality: 0, compliance: 0, infrastructure: 0 };
    base.forEach(f => {
      const cat = classifyFinding(f);
      if (counts[cat] !== undefined) counts[cat]++;
    });
    return counts;
  }, [findings, fileFilter, sevFilter, searchQ, triageFilter, triageResults, ruleFilter]);

  const totalPages = Math.ceil(filteredFindings.length / pageSize) || 1;
  const pagedFindings = filteredFindings.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };

  /* ── reset page when filters change ────────────────────────────────────── */
  useEffect(() => { setPage(1); }, [sevFilter, searchQ, sortCol, fileFilter, ruleFilter, categoryFilter]);

  /* ── file tree renderer ─────────────────────────────────────────────────── */
  const countDirFindings = (node) => {
    let count = 0;
    Object.entries(node).forEach(([k, v]) => {
      if (k.startsWith('__')) return;
      if (v.__file) count += v.__count || 0;
      else count += countDirFindings(v);
    });
    return count;
  };

  const renderFileTree = (node, prefix = '', level = 0) => {
    const entries = Object.entries(node).filter(([k]) => !k.startsWith('__'));
    const dirs = entries.filter(([, v]) => v.__dir || (!v.__file && typeof v === 'object'));
    const files = entries.filter(([, v]) => v.__file);

    return [...dirs.sort((a, b) => a[0].localeCompare(b[0])), ...files.sort((a, b) => a[0].localeCompare(b[0]))].map(([name, val]) => {
      const fullPath = prefix ? `${prefix}/${name}` : name;
      if (val.__file) {
        const isActive = fileFilter === val.__path;
        return (
          <div
            key={fullPath}
            className={`vigil-file-tree-file ${isActive ? 'active' : ''}`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
            onClick={() => setFileFilter(isActive ? null : val.__path)}
            title={val.__path}
          >
            <span className="vigil-tree-icon">📄</span>
            <span className="vigil-tree-name">{name}</span>
            <span className="vigil-tree-count">{val.__count}</span>
            <span className="vigil-tree-sev-dot" style={{ background: SEV[val.__maxSev]?.color || '#6b7280' }} />
          </div>
        );
      }
      const isExpanded = expandedDirs[fullPath] !== false;
      const childFindings = countDirFindings(val);
      return (
        <div key={fullPath}>
          <div
            className="vigil-file-tree-dir"
            style={{ paddingLeft: `${level * 16 + 8}px` }}
            onClick={() => toggleDir(fullPath)}
          >
            <span className="vigil-tree-arrow">{isExpanded ? '▾' : '▸'}</span>
            <span className="vigil-tree-icon">📁</span>
            <span className="vigil-tree-name">{name}</span>
            {childFindings > 0 && <span className="vigil-tree-count">{childFindings}</span>}
          </div>
          {isExpanded && renderFileTree(val, fullPath, level + 1)}
        </div>
      );
    });
  };

  /* ══════════════════════════════════════════════════════════════════════════ */
  /*  RENDER                                                                   */
  /* ══════════════════════════════════════════════════════════════════════════ */

  /* ── header (always shown) ─────────────────────────────────────────────── */
  const header = (
    <div className="vigil-header mb-4">
      <h2 className="fw-bold mb-1">
        <span className="vigil-shield-icon me-2">🛡️</span>
        {t('vigilScan.title')}
      </h2>
      <p className="text-muted mb-0">{t('vigilScan.subtitle')}</p>
    </div>
  );

  /* ═════════════ MODE: SETUP ═════════════ */
  if (mode === 'setup') {
    return (
      <motion.div className="vigil-scan" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        {header}

        <div className="vigil-card vigil-setup-card mb-4">
          <div className="mb-3">
            <label className="form-label fw-semibold">{t('vigilScan.repoPath')}</label>
            <div className="input-group">
              <span className="input-group-text">📁</span>
              <input
                type="text" className="form-control vigil-input"
                placeholder={t('vigilScan.repoPathPlaceholder')}
                value={repoPath} onChange={e => setRepoPath(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && startScan()}
              />
            </div>
          </div>

          {/* Engine selector */}
          <div className="mb-3">
            <label className="form-label fw-semibold">Engine</label>
            <div className="vigil-engine-selector">
              {[
                { id: 'opengrep', label: 'OpenGrep', icon: '⚡', desc: 'Fast pattern matching (default)' },
                { id: 'joern',    label: 'Joern',    icon: '🔗', desc: 'Taint flow & cross-file analysis' },
                { id: 'auto',     label: 'Auto',     icon: '🤖', desc: 'Best engine per rule type' },
                { id: 'multi',    label: 'Multi',    icon: '🔀', desc: 'All engines, merged results' },
              ].map(e => (
                <button
                  key={e.id}
                  className={`vigil-engine-btn ${selectedEngine === e.id ? 'vigil-engine-btn--active' : ''}`}
                  onClick={() => setSelectedEngine(e.id)}
                  title={e.desc}
                >
                  <span className="vigil-engine-btn__icon">{e.icon}</span>
                  <span className="vigil-engine-btn__label">{e.label}</span>
                </button>
              ))}
            </div>
            {selectedEngine === 'joern' && (
              <div className="vigil-engine-hint">
                🔗 Joern enables <strong>Taint Flow Graph</strong> — shows the full source→sink data flow path in findings.
                Scan takes 2–4 min (CPG build). Make sure <code>joern</code> is installed.
              </div>
            )}
          </div>

          {scanProfiles.length > 0 && (
            <div className="mb-3">
              <label className="form-label fw-semibold">Scan Profile</label>
              <div className="vigil-profile-cards">
                {scanProfiles.map(p => (
                  <div
                    key={p.id}
                    className={`vigil-profile-card ${activeProfile === p.id ? 'vigil-profile-card--active' : ''}`}
                    onClick={() => selectProfile(p)}
                  >
                    <div className="vigil-profile-card__icon">{p.icon}</div>
                    <div className="vigil-profile-card__name">{p.name}</div>
                    <div className="vigil-profile-card__desc">{p.description}</div>
                    {activeProfile === p.id && <div className="vigil-profile-card__check">✓</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {rulePacks.length > 0 && (
            <div className="mb-3">
              <label className="form-label fw-semibold">{t('vigilScan.selectPacks')}</label>
              <LanguageGroupedPacks
                rulePacks={rulePacks}
                selectedPacks={selectedPacks}
                togglePack={togglePack}
                toggleAllPacks={toggleAllPacks}
              />
            </div>
          )}

          <button className="btn vigil-btn-start" onClick={startScan} disabled={!repoPath.trim()}>
            🛡️ {t('vigilScan.startScan')}
          </button>
        </div>

        {/* recent scans */}
        <div className="vigil-card">
          <h5 className="fw-semibold mb-3">{t('vigilScan.recentScans')}</h5>
          {recentScans.length === 0 ? (
            <p className="text-muted">{t('vigilScan.noScans')}</p>
          ) : (
            <>
            <div className="table-responsive">
              <table className="table table-hover vigil-table mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{t('vigilScan.repoPath')}</th>
                    <th>{t('vigilScan.severity')}</th>
                    <th>{t('vigilScan.totalFindings')}</th>
                    <th>{t('vigilScan.duration')}</th>
                    <th>When</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {recentScans.map(s => (
                    <tr key={s.id}>
                      <td className="font-mono text-muted">{(s.id || '').slice(0, 8)}</td>
                      <td>{shortenPath(s.path || s.repoPath, 50)}</td>
                      <td>
                        {s.critical > 0 && <span className="badge vigil-badge-critical me-1">{s.critical} C</span>}
                        {s.high > 0 && <span className="badge vigil-badge-high me-1">{s.high} H</span>}
                        {s.medium > 0 && <span className="badge vigil-badge-warning me-1">{s.medium} M</span>}
                      </td>
                      <td>{s.total ?? '—'}</td>
                      <td>{formatDuration(s.duration)}</td>
                      <td className="text-muted small">{relativeTime(s.createdAt || s.startedAt)}</td>
                      <td>
                        <div className="d-flex gap-1">
                          <button className="btn btn-sm vigil-btn-outline" title="Rescan from this path" onClick={() => rescanFrom(s)}>🔄</button>
                          <button className="btn btn-sm vigil-btn-outline" onClick={() => viewScan(s.id)}>
                            {t('vigilScan.viewResults')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-end"><Link to="/vigil/history" className="small text-muted">View all scan history →</Link></div>
            </>
          )}
        </div>
      </motion.div>
    );
  }

  /* ═════════════ MODE: SCANNING ═════════════ */
  if (mode === 'scanning') {
    return (
      <motion.div className="vigil-scan" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}>
        {header}

        <div className="vigil-card vigil-progress-card text-center mb-4">
          {/* animated shield */}
          <motion.div
            className="vigil-shield-anim"
            animate={{ scale: [1, 1.08, 1], opacity: [0.85, 1, 0.85] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            🛡️
          </motion.div>

          <motion.p
            className="vigil-status-text mt-3"
            key={statusMsg}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            {statusMsg}
          </motion.p>

          <div className="vigil-elapsed-time mt-2">
            <span className="vigil-elapsed-label">Elapsed: </span>
            <span className="vigil-elapsed-value font-mono">
              {String(Math.floor(elapsedTime / 60)).padStart(2, '0')}:{String(elapsedTime % 60).padStart(2, '0')}
            </span>
          </div>

          {/* phase indicators */}
          <div className="vigil-phases mt-4">
            {PHASES.map((ph, i) => (
              <div key={ph} className={`vigil-phase ${i < phase ? 'done' : ''} ${i === phase ? 'active' : ''}`}>
                <span className="vigil-phase-num">
                  {i < phase ? '✓' : i + 1}
                </span>
                <span className="vigil-phase-label">{t(`vigilScan.${ph}`)}</span>
              </div>
            ))}
          </div>

          {/* warning badges */}
          {warnings.length > 0 && (
            <div className="mt-3">
              <span className="badge vigil-badge-warning">
                ⚠️ {warnings.length} {t('vigilScan.warnings')}
              </span>
            </div>
          )}
        </div>

        {/* live log terminal */}
        <div className="vigil-card vigil-log-card">
          <div className="vigil-log-header">
            <span className="vigil-log-dot red"></span>
            <span className="vigil-log-dot yellow"></span>
            <span className="vigil-log-dot green"></span>
            <span className="ms-2 text-muted small">Vigil Output</span>
          </div>
          <div className="vigil-log-body" ref={logBoxRef}>
            <AnimatePresence initial={false}>
              {logs.map((log, i) => (
                <motion.div
                  key={i}
                  className={`vigil-log-line ${log.type}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {log.type === 'warning' && <span className="vigil-log-tag warning">WARN</span>}
                  {log.type === 'error' && <span className="vigil-log-tag error">ERR</span>}
                  {log.type === 'complete' && <span className="vigil-log-tag complete">DONE</span>}
                  {log.message}
                </motion.div>
              ))}
            </AnimatePresence>
            {mode === 'scanning' && (
              <motion.span
                className="vigil-cursor"
                animate={{ opacity: [1, 0] }}
                transition={{ duration: 0.7, repeat: Infinity }}
              >▊</motion.span>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  /* ═════════════ MODE: RESULTS ═════════════ */
  const total = findings.length;

  return (
    <motion.div className="vigil-scan" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
      {header}

      {/* ── Scan Summary Banner ────────────────────────────────────────────── */}
      <motion.div className="vigil-scan-banner mb-4" initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <div className="vigil-scan-banner-grade" style={{ background: GRADE_COLORS[riskInfo.grade] }}>
          {riskInfo.grade}
        </div>
        <div className="vigil-scan-banner-info">
          <div className="vigil-scan-banner-path">
            📁 {shortenPath(scanResult?.path || scanResult?.repoPath || repoPath, 70)}
          </div>
          <div className="vigil-scan-banner-meta">
            <span>Risk Score: <strong>{riskInfo.score}</strong></span>
            <span className="vigil-banner-sep">·</span>
            <span>{formatDuration(scanResult?.duration)}</span>
            <span className="vigil-banner-sep">·</span>
            <span><strong>{findings.length}</strong> findings</span>
            {(scanResult?.rulesLoaded || scanResult?.rulesCompiled) && (
              <>
                <span className="vigil-banner-sep">·</span>
                <span>{scanResult.rulesLoaded || scanResult.rulesCompiled} rules</span>
              </>
            )}
          </div>
        </div>
        <div className="vigil-scan-banner-actions">
          <button className="vigil-btn-outline btn btn-sm" onClick={rescan}>
            🔄 New Scan
          </button>
        </div>
      </motion.div>

      {/* ── Row 1: severity summary cards ──────────────────────────────────── */}
      <div className="vigil-summary-row mb-4">
        {SEV_KEYS.map((sev, i) => (
          <motion.div
            key={sev}
            className="vigil-sev-card"
            style={{ '--sev-color': SEV[sev].color, '--sev-bg': SEV[sev].bg }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07, duration: 0.35 }}
          >
            <div className="vigil-sev-icon">{SEV[sev].icon}</div>
            <div className="vigil-sev-count"><AnimatedCounter value={sevCounts[sev]} /></div>
            <div className="vigil-sev-label">{t(`vigilScan.${sev}`)}</div>
          </motion.div>
        ))}
      </div>

      {/* ── Row 2: donut + top rules ───────────────────────────────────────── */}
      <div className="vigil-chart-row mb-4">
        <motion.div className="vigil-card vigil-chart-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
          <h6 className="fw-semibold mb-3">{t('vigilScan.severityDistribution')}</h6>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={pieData} dataKey="value" nameKey="name"
                cx="50%" cy="50%" innerRadius={65} outerRadius={100}
                paddingAngle={2} animationDuration={800}
              >
                {pieData.map(d => <Cell key={d.name} fill={SEV[d.name]?.color || '#999'} />)}
              </Pie>
              <Tooltip formatter={(val, name) => [val, t(`vigilScan.${name}`)]} />
              <Legend formatter={(val) => t(`vigilScan.${val}`)} />
            </PieChart>
          </ResponsiveContainer>
          <div className="vigil-donut-center">
            <span className="vigil-donut-total"><AnimatedCounter value={total} /></span>
            <span className="vigil-donut-label">{t('vigilScan.totalFindings')}</span>
          </div>
        </motion.div>

        <motion.div className="vigil-card vigil-chart-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}>
          <h6 className="fw-semibold mb-3">{t('vigilScan.topRules')}</h6>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topRules} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="var(--accent)" radius={[0, 4, 4, 0]} animationDuration={800} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* ── Row 3: top files + severity by category ────────────────────────── */}
      <div className="vigil-chart-row mb-4">
        <motion.div className="vigil-card vigil-chart-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
          <h6 className="fw-semibold mb-3">{t('vigilScan.topFiles')}</h6>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={topFiles} layout="vertical" margin={{ left: 10, right: 20 }}>
              <XAxis type="number" allowDecimals={false} />
              <YAxis type="category" dataKey="name" width={180} tick={{ fontSize: 11 }} />
              <Tooltip formatter={(val, _name, props) => [val, props.payload.fullPath]} />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 4, 4, 0]} animationDuration={800} />
            </BarChart>
          </ResponsiveContainer>
        </motion.div>

        <motion.div className="vigil-card vigil-chart-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <h6 className="fw-semibold mb-3">{t('vigilScan.severityByCategory')}</h6>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={sevByCategory} margin={{ left: 10, right: 20 }}>
              <XAxis dataKey="category" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {SEV_KEYS.map(sev => (
                <Bar key={sev} dataKey={sev} stackId="a" fill={SEV[sev].color} animationDuration={800} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </motion.div>
      </div>

      {/* ── Row 3b: top affected files + rule coverage ───────────────────── */}
      <div className="vigil-chart-row mb-4">
        <motion.div className="vigil-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.42 }}>
          <h6 className="fw-semibold mb-3">📂 Top Affected Files</h6>
          <div className="vigil-top-files-list">
            {topAffectedFiles.map((f, i) => (
              <div key={i} className="vigil-top-file-item" onClick={() => { setFileFilter(f.path); setPage(1); }}>
                <div className="vigil-top-file-name font-mono">{f.name}</div>
                <div className="vigil-top-file-bar-wrap">
                  <div className="vigil-top-file-bar">
                    {SEV_KEYS.map(s => {
                      const w = f.count > 0 ? ((f.sevs[s] || 0) / f.count) * 100 : 0;
                      return w > 0 ? <div key={s} className="vigil-top-file-bar-seg" style={{ width: `${w}%`, background: SEV[s].color }} /> : null;
                    })}
                  </div>
                </div>
                <span className="vigil-top-file-count">{f.count}</span>
              </div>
            ))}
            {topAffectedFiles.length === 0 && <p className="text-muted mb-0">No files</p>}
          </div>
        </motion.div>
        <motion.div className="vigil-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.44 }}>
          <h6 className="fw-semibold mb-3">📦 Rule Coverage</h6>
          <div className="vigil-rule-coverage">
            <div className="vigil-rule-coverage-stat">
              <span className="vigil-rule-coverage-num">{ruleCoverage.active}</span>
              <span className="vigil-rule-coverage-label">Packs with findings</span>
            </div>
            <div className="vigil-rule-coverage-stat">
              <span className="vigil-rule-coverage-num">{ruleCoverage.total}</span>
              <span className="vigil-rule-coverage-label">Total packs scanned</span>
            </div>
            <div className="vigil-rule-coverage-bar-wrap">
              <div className="vigil-rule-coverage-bar" style={{ width: ruleCoverage.total > 0 ? `${(ruleCoverage.active / ruleCoverage.total) * 100}%` : '0%' }} />
            </div>
            <div className="text-muted" style={{ fontSize: '0.82rem' }}>
              {ruleCoverage.total > 0 ? `${Math.round((ruleCoverage.active / ruleCoverage.total) * 100)}% of packs produced findings` : 'No pack data available'}
            </div>
          </div>
        </motion.div>
      </div>

      {/* ── Row 4: file tree + findings table ──────────────────────────── */}
      <div className="vigil-results-layout mb-4">
        {/* File tree panel */}
        <motion.div className="vigil-file-tree" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.45 }}>
          <div className="vigil-file-tree-header">
            <h6 className="fw-semibold mb-0">📁 Files</h6>
            {fileFilter && (
              <button className="vigil-file-tree-clear" onClick={() => { setFileFilter(null); setPage(1); }}>
                ✕ Clear
              </button>
            )}
          </div>
          <div className="vigil-file-tree-body">
            {renderFileTree(fileTree)}
          </div>
        </motion.div>

        {/* Findings table */}
        <motion.div className="vigil-card vigil-findings-area" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.45 }}>
        <div className="d-flex flex-wrap align-items-center justify-content-between mb-3 gap-2">
          <h5 className="fw-semibold mb-0">{t('vigilScan.findingsTable')}</h5>
          <div className="d-flex gap-2">
            <button className="btn btn-sm vigil-btn-outline" onClick={exportCsv}>
              📥 {t('vigilScan.exportCsv')}
            </button>
            <button className="btn btn-sm vigil-btn-outline" onClick={exportJson}>
              📥 Export JSON
            </button>
            <button className="btn btn-sm vigil-btn-outline" onClick={exportFilteredJson}>
              📥 Export Filtered JSON
            </button>
            <button className="btn btn-sm vigil-btn-outline" onClick={exportSarif}>
              📤 Export SARIF
            </button>
          </div>
        </div>

        {/* ── Triage action bar ──────────────────────────────────── */}
        {selectedFindings.size > 0 && !triageLoading && (
          <div className="vigil-triage-bar mb-3">
            <span className="vigil-triage-bar-text">{selectedFindings.size} finding{selectedFindings.size !== 1 ? 's' : ''} selected</span>
            <button className="vigil-triage-btn" onClick={runBulkTriage}>
              🤖 AI Triage
            </button>
            <button className="vigil-triage-clear-btn" onClick={() => setSelectedFindings(new Set())}>
              Clear Selection
            </button>
          </div>
        )}

        {/* ── Triage loading ─────────────────────────────────────── */}
        {triageLoading && (
          <div className="vigil-triage-loading mb-3">
            <div className="vigil-pulse-dot" />
            <span>AI is analyzing {selectedFindings.size} finding{selectedFindings.size !== 1 ? 's' : ''}…</span>
          </div>
        )}

        {/* ── Triage error ───────────────────────────────────────── */}
        {triageError && (
          <div className="vigil-triage-error mb-3">
            <span>⚠️ Triage failed: {triageError}</span>
            <button className="vigil-triage-clear-btn" onClick={() => setTriageError(null)}>Dismiss</button>
          </div>
        )}

        {/* ── Triage results panel ───────────────────────────────── */}
        {triageResults && (
          <div className="vigil-triage-panel mb-3">
            <div className="vigil-triage-panel-header">
              <h6 className="fw-semibold mb-0">🤖 AI Triage Results</h6>
              <div className="vigil-triage-panel-meta">
                {triageResults.provider && <span className="vigil-meta-badge">{triageResults.provider}</span>}
                {triageResults.model && <span className="vigil-meta-badge">{triageResults.model}</span>}
              </div>
              <button className="vigil-triage-clear-btn" onClick={clearTriageState}>✕ Close</button>
            </div>
            <div className="vigil-triage-stats">
              <span className="vigil-triage-stat-badge vigil-triage-stat-escalate">🔴 Escalate: {triageResults.stats?.escalate || 0}</span>
              <span className="vigil-triage-stat-badge vigil-triage-stat-alert">🟡 Alert: {triageResults.stats?.alert || 0}</span>
              <span className="vigil-triage-stat-badge vigil-triage-stat-suppress">🟢 Suppress: {triageResults.stats?.suppress || 0}</span>
              <span className="vigil-triage-stat-badge vigil-triage-stat-group">🔵 Group: {triageResults.stats?.group || 0}</span>
              {(triageResults.stats?.falsePositives || 0) > 0 && (
                <span className="vigil-triage-stat-badge vigil-triage-stat-fp">🟠 FP detected: {triageResults.stats.falsePositives}</span>
              )}
            </div>
            <div className="vigil-triage-bulk-actions">
              <button className="btn btn-sm vigil-btn-outline" onClick={() => acceptAllOfAction('suppress')}>
                ✓ Accept All Suppressions ({Object.values(triageResults.decisions).filter(d => d.action === 'suppress').length})
              </button>
              <button className="btn btn-sm vigil-btn-outline" onClick={acceptAll}>
                ✓ Accept All
              </button>
              <span className="vigil-triage-counter">
                {triageAcceptedCount} accepted · {triagePendingCount} pending
              </span>
            </div>
            <div className="vigil-triage-filters">
              {['all', 'escalate', 'alert', 'suppress', 'group'].map(f => (
                <button
                  key={f}
                  className={`vigil-triage-filter-btn ${triageFilter === f ? 'active' : ''}`}
                  onClick={() => { setTriageFilter(f); setPage(1); }}
                >
                  {f === 'all' ? 'Show All' : f.charAt(0).toUpperCase() + f.slice(1) + ' Only'}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* severity filter chips */}
        <div className="vigil-filter-chips mb-3">
          {['all', ...SEV_KEYS].map(s => (
            <button
              key={s}
              className={`vigil-chip ${sevFilter === s ? 'active' : ''}`}
              style={s !== 'all' ? { '--chip-color': SEV[s]?.color } : {}}
              onClick={() => { setSevFilter(s); setPage(1); }}
            >
              {s === 'all' ? `All (${total})` : `${t(`vigilScan.${s}`)} (${sevCounts[s]})`}
            </button>
          ))}
        </div>

        {/* category filter chips */}
        <div className="vigil-category-filters mb-3">
          {[
            { key: 'all', icon: '', label: 'All' },
            { key: 'security', icon: '🔒', label: 'Security' },
            { key: 'quality', icon: '📐', label: 'Quality' },
            { key: 'compliance', icon: '📋', label: 'Compliance' },
            { key: 'infrastructure', icon: '🐳', label: 'Infra' },
          ].map(c => (
            <button
              key={c.key}
              className={`vigil-category-filter-btn ${categoryFilter === c.key ? 'vigil-category-filter-btn--active' : ''}`}
              onClick={() => { setCategoryFilter(c.key); setPage(1); }}
            >
              {c.icon}{c.icon ? ' ' : ''}{c.label} ({categoryCounts[c.key] || 0})
            </button>
          ))}
        </div>

        {/* file filter indicator */}
        {fileFilter && (
          <div className="vigil-file-filter-indicator mb-2">
            <span>Filtered to: <code className="font-mono">{shortenPath(fileFilter, 50)}</code></span>
            <button className="vigil-copy-btn ms-2" onClick={() => { setFileFilter(null); setPage(1); }}>✕ Clear</button>
          </div>
        )}

        {/* rule filter indicator */}
        {ruleFilter && (
          <div className="vigil-file-filter-indicator mb-2">
            <span>Rule: <code className="font-mono">{ruleFilter}</code></span>
            <button className="vigil-copy-btn ms-2" onClick={() => { setRuleFilter(null); setPage(1); }}>✕ Clear</button>
          </div>
        )}

        {/* search */}
        <div className="mb-3">
          <input
            type="text" className="form-control vigil-input"
            placeholder={t('vigilScan.searchFindings')}
            value={searchQ} onChange={e => { setSearchQ(e.target.value); setPage(1); }}
          />
        </div>

        {/* row count */}
        <div className="vigil-row-count mb-2">
          Showing <strong>{filteredFindings.length === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, filteredFindings.length)}</strong> of <strong>{filteredFindings.length}</strong> findings
          {filteredFindings.length !== findings.length && (
            <span className="text-muted"> ({findings.length} total)</span>
          )}
        </div>

        {/* table */}
        <div className="table-responsive">
          <table className="table vigil-table vigil-findings-table mb-0">
            <thead>
              <tr>
                <th className="vigil-checkbox-col" style={{ width: '36px' }}>
                  <input
                    type="checkbox"
                    checked={filteredFindings.length > 0 && selectedFindings.size === filteredFindings.length}
                    onChange={(e) => { e.stopPropagation(); toggleSelectAll(); }}
                    title="Select all / Deselect all"
                  />
                </th>
                {[
                  { col: 'severity', label: t('vigilScan.severity') },
                  { col: 'ruleId', label: t('vigilScan.ruleId') },
                  { col: 'file', label: t('vigilScan.file') },
                  { col: 'line', label: t('vigilScan.line') },
                  { col: 'message', label: t('vigilScan.message') },
                  { col: 'confidence', label: t('vigilScan.confidence') },
                ].map(h => (
                  <th key={h.col} className="vigil-sortable" onClick={() => toggleSort(h.col)}>
                    {h.label} {sortCol === h.col ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pagedFindings.map((f, i) => {
                const sev = (f.severity || 'info').toLowerCase();
                const fid = getFindingId(f);
                const isExpanded = expandedRow === fid;
                const triageDecision = triageResults?.decisions?.[fid];
                const triageStatus = triageAccepted[fid];
                const isSuppressed = triageDecision?.action === 'suppress' && triageStatus === 'accepted';
                const isEscalated = triageDecision?.action === 'escalate' && triageStatus === 'accepted';
                return (
                  <React.Fragment key={fid}>
                    <tr
                      className={`vigil-finding-row vigil-finding-row-${sev} ${isExpanded ? 'expanded' : ''} ${isSuppressed ? 'vigil-finding--suppressed' : ''} ${isEscalated ? 'vigil-finding--escalated' : ''}`}
                      onClick={() => {
                        setExpandedRow(isExpanded ? null : fid);
                        if (!isExpanded && f.ruleId && !ruleCacheRef.current[f.ruleId]) fetchRuleDetail(f.ruleId);
                      }}
                      style={{ cursor: 'pointer' }}
                    >
                      <td className="vigil-checkbox-col" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedFindings.has(fid)}
                          onChange={() => toggleFindingSelection(fid)}
                        />
                      </td>
                      <td>
                        <span className="badge" style={{ background: SEV[sev]?.color, color: '#fff' }}>{t(`vigilScan.${sev}`)}</span>
                        {triageDecision && (
                          <span className={`vigil-triage-badge vigil-triage-badge--${triageDecision.action}`} title={triageDecision.reasoning}>
                            {triageDecision.action.charAt(0).toUpperCase() + triageDecision.action.slice(1)}
                            <span className="vigil-triage-conf">{Math.round(triageDecision.confidence * 100)}%</span>
                          </span>
                        )}
                        {triageDecision?.falsePositive && (
                          <span className="vigil-triage-badge vigil-triage-badge--fp">FP</span>
                        )}
                      </td>
                      <td className="font-mono">
                        {f.ruleId}
                        <button
                          className="vigil-filter-rule-btn"
                          title={ruleFilter === f.ruleId ? 'Clear rule filter' : 'Filter by this rule'}
                          onClick={(e) => { e.stopPropagation(); setRuleFilter(ruleFilter === f.ruleId ? null : f.ruleId); setPage(1); }}
                        >
                          {ruleFilter === f.ruleId ? '✕' : '⊟'}
                        </button>
                        {(() => {
                          const cat = classifyFinding(f);
                          const catInfo = { security: { icon: '🔒', label: 'Security' }, quality: { icon: '📐', label: 'Quality' }, compliance: { icon: '📋', label: 'Compliance' }, infrastructure: { icon: '🐳', label: 'Infra' } };
                          const info = catInfo[cat] || catInfo.quality;
                          return <span className={`vigil-category-badge vigil-category-badge--${cat}`}>{info.icon} {info.label}</span>;
                        })()}
                      </td>
                      <td className="font-mono" title={f.file}>{shortenPath(f.file, 35)}</td>
                      <td>{f.startLine}</td>
                      <td className="vigil-msg-cell">
                        {f.message}
                        {triageDecision && !triageStatus && (
                          <span className="vigil-triage-actions" onClick={(e) => e.stopPropagation()}>
                            <button className="vigil-triage-accept-btn" onClick={() => acceptTriageDecision(fid)} title="Accept">✓</button>
                            <button className="vigil-triage-reject-btn" onClick={() => rejectTriageDecision(fid)} title="Reject">✗</button>
                          </span>
                        )}
                        {triageStatus === 'accepted' && <span className="vigil-triage-status vigil-triage-status--accepted">✓ Accepted</span>}
                        {triageStatus === 'rejected' && <span className="vigil-triage-status vigil-triage-status--rejected">✗ Rejected</span>}
                      </td>
                      <td><span className={`badge vigil-conf-${(f.confidence || '').toLowerCase()}`}>{f.confidence}</span></td>
                    </tr>
                    {isExpanded && (() => {
                      const rd = ruleDetails[f.ruleId];
                      const sevKey = (f.severity || 'info').toLowerCase();
                      return (
                        <tr className="vigil-expanded-row">
                          <td colSpan={7} style={{ padding: 0 }}>
                            <div className="vigil-detail-panel">
                              {/* Code snippet */}
                              {f.snippet && (
                                <div className="vigil-code-block">
                                  <div className="vigil-code-header">
                                    <span className="font-mono" style={{ fontSize: '0.78rem', opacity: 0.7 }}>
                                      {shortenPath(f.file, 60)}
                                    </span>
                                  </div>
                                  <pre className="vigil-code-pre">
                                    <code>
                                      {f.snippet.split('\n').map((line, li) => {
                                        const lineNum = (f.startLine || 1) + li;
                                        const isTarget = lineNum >= (f.startLine || 0) && lineNum <= (f.endLine || f.startLine || 0);
                                        return (
                                          <div key={li} className={`vigil-code-line ${isTarget ? `vigil-code-highlight vigil-code-highlight-${sevKey}` : ''}`}>
                                            <span className="vigil-line-num">{lineNum}</span>
                                            <span className="vigil-line-content">{line}</span>
                                          </div>
                                        );
                                      })}
                                    </code>
                                  </pre>
                                </div>
                              )}

                              {/* ── Taint Flow Graph (flow findings) ──── */}
                              {f.flowPath && f.flowPath.length > 0 && (
                                <TaintFlowGraph
                                  flowPath={f.flowPath}
                                  ruleId={f.ruleId}
                                  severity={f.severity}
                                />
                              )}

                              {/* ── Pattern Context Card (pattern/concept findings) ── */}
                              {(!f.flowPath || f.flowPath.length === 0) && (
                                <PatternContextCard
                                  captures={f.captures}
                                  context={f.context}
                                  file={f.file}
                                  line={f.startLine}
                                />
                              )}

                              {/* Metadata grid */}
                              <div className="vigil-detail-meta">
                                <div className="vigil-detail-meta-item">
                                  <span className="vigil-detail-label">Rule ID</span>
                                  <Link to={`/vigil/rules/${f.ruleId}`} className="vigil-rule-link font-mono">{f.ruleId}</Link>
                                </div>
                                <div className="vigil-detail-meta-item">
                                  <span className="vigil-detail-label">{t('vigilScan.severity')}</span>
                                  <span className="vigil-meta-badge" style={{ background: SEV[sevKey]?.color || '#6b7280' }}>{t(`vigilScan.${sevKey}`)}</span>
                                </div>
                                <div className="vigil-detail-meta-item">
                                  <span className="vigil-detail-label">{t('vigilScan.confidence')}</span>
                                  <span className={`vigil-meta-badge vigil-conf-${(f.confidence || '').toLowerCase()}`}>{f.confidence}</span>
                                </div>
                                <div className="vigil-detail-meta-item">
                                  <span className="vigil-detail-label">Engine</span>
                                  <span className="vigil-meta-badge" style={{ background: '#6366f1' }}>{f.engine || 'unknown'}</span>
                                </div>
                                <div className="vigil-detail-meta-item">
                                  <span className="vigil-detail-label">{t('vigilScan.file')}</span>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <code className="font-mono" style={{ fontSize: '0.82rem', wordBreak: 'break-all' }}>{f.file}</code>
                                    <button className="vigil-copy-btn" onClick={(e) => { e.stopPropagation(); copyToClipboard(f.file); }} title="Copy path">
                                      {copiedPath === f.file ? '✓' : '📋'}
                                    </button>
                                  </div>
                                </div>
                                <div className="vigil-detail-meta-item">
                                  <span className="vigil-detail-label">Location</span>
                                  <span className="font-mono">{f.startLine}:{f.startColumn} – {f.endLine}:{f.endColumn}</span>
                                </div>
                              </div>

                              {/* CWE & OWASP */}
                              {rd && (rd.cwe?.length > 0 || rd.owasp?.length > 0) && (
                                <div className="vigil-detail-tags">
                                  {(rd.cwe || []).map(cwe => {
                                    const num = String(cwe).replace(/^CWE-?/i, '');
                                    return (
                                      <a key={cwe} href={`https://cwe.mitre.org/data/definitions/${num}.html`} target="_blank" rel="noopener noreferrer" className="vigil-meta-badge vigil-cwe-badge">
                                        CWE-{num}
                                      </a>
                                    );
                                  })}
                                  {(rd.owasp || []).map(o => (
                                    <span key={o} className="vigil-meta-badge vigil-owasp-badge">{o}</span>
                                  ))}
                                </div>
                              )}

                              {/* Fix suggestion */}
                              {rd?.fix && (
                                <div className="vigil-fix-box">
                                  <span className="vigil-fix-icon">💡</span>
                                  <div>
                                    <strong>Fix Suggestion</strong>
                                    <p style={{ margin: '0.25rem 0 0' }}>{rd.fix}</p>
                                  </div>
                                </div>
                              )}

                              {/* Message */}
                              <div className="vigil-detail-message">
                                <strong>{t('vigilScan.message')}:</strong> {f.message}
                              </div>

                              {/* ── AI Investigation ─────────────────── */}
                              {(() => {
                                const fid = `${f.ruleId}::${f.file}::${f.startLine}`;
                                const inv = investigationResults[fid];
                                const loading = investigatingIds[fid];
                                const error = investigationErrors[fid];
                                return (
                                  <div className="vigil-investigate-section">
                                    {!inv && !loading && (
                                      <button
                                        className="vigil-investigate-btn"
                                        onClick={(e) => { e.stopPropagation(); investigateFinding(f); }}
                                      >
                                        🔍 {t('vigilScan.investigate')}
                                      </button>
                                    )}
                                    {loading && (
                                      <div className="vigil-investigation-loading">
                                        <div className="vigil-pulse-dot" />
                                        <span>{t('vigilScan.investigating')}</span>
                                      </div>
                                    )}
                                    {error && !loading && (
                                      <div className="vigil-investigation-error">
                                        <span>⚠️ {t('vigilScan.investigateError')}: {error}</span>
                                        <button
                                          className="vigil-investigate-btn vigil-retry-btn"
                                          onClick={(e) => { e.stopPropagation(); investigateFinding(f); }}
                                        >
                                          {t('vigilScan.retry')}
                                        </button>
                                      </div>
                                    )}
                                    {inv && (
                                      <div className="vigil-investigation">
                                        <h6 className="vigil-investigation-title">🤖 {t('vigilScan.investigation')}</h6>
                                        {inv.summary && (
                                          <div className="vigil-investigation-summary">{inv.summary}</div>
                                        )}
                                        <div className="vigil-investigation-badges">
                                          {inv.exploitability && (
                                            <span className={`vigil-exploitability-badge vigil-exploit-${inv.exploitability.toLowerCase()}`}>
                                              {t('vigilScan.exploitability')}: {inv.exploitability}
                                            </span>
                                          )}
                                          {inv.priority && (
                                            <span className={`vigil-priority-badge vigil-priority-${inv.priority.toLowerCase()}`}>
                                              {t('vigilScan.priority')}: {inv.priority}
                                            </span>
                                          )}
                                        </div>
                                        {inv.dataFlow && (
                                          <div className="vigil-investigation-dataflow">
                                            <strong>{t('vigilScan.dataFlow')}</strong>
                                            <pre className="vigil-dataflow-pre">{inv.dataFlow}</pre>
                                          </div>
                                        )}
                                        {inv.falsePositive && (
                                          <div className={`vigil-fp-assessment ${inv.falsePositive.likely ? 'vigil-fp-likely' : 'vigil-fp-real'}`}>
                                            <strong>{t('vigilScan.falsePositive')}</strong>
                                            <span className="vigil-fp-verdict">
                                              {inv.falsePositive.likely ? `✅ ${t('vigilScan.likelyFP')}` : `🔴 ${t('vigilScan.realIssue')}`}
                                            </span>
                                            {inv.falsePositive.reasoning && (
                                              <p className="vigil-fp-reasoning">{inv.falsePositive.reasoning}</p>
                                            )}
                                          </div>
                                        )}
                                        {inv.fix && (
                                          <div className="vigil-investigation-fix">
                                            <strong>{t('vigilScan.fixSuggestion')}</strong>
                                            {inv.fix.description && <p>{inv.fix.description}</p>}
                                            {inv.fix.codeExample && (
                                              <pre className="vigil-investigation-fix-code"><code>{inv.fix.codeExample}</code></pre>
                                            )}
                                          </div>
                                        )}
                                        {inv.relatedCWEs?.length > 0 && (
                                          <div className="vigil-investigation-cwes">
                                            <strong>{t('vigilScan.relatedCWEs')}: </strong>
                                            {inv.relatedCWEs.map(cwe => {
                                              const num = String(cwe).replace(/^CWE-?/i, '');
                                              return (
                                                <a key={cwe} href={`https://cwe.mitre.org/data/definitions/${num}.html`} target="_blank" rel="noopener noreferrer" className="vigil-meta-badge vigil-cwe-badge">
                                                  CWE-{num}
                                                </a>
                                              );
                                            })}
                                          </div>
                                        )}
                                        <div className="vigil-investigation-meta">
                                          {inv.provider && <span className="vigil-meta-badge">{inv.provider}</span>}
                                          {inv.model && <span className="vigil-meta-badge">{inv.model}</span>}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}

                              {loadingRules[f.ruleId] && <div className="text-muted" style={{ fontSize: '0.82rem' }}>Loading rule details…</div>}
                            </div>
                          </td>
                        </tr>
                      );
                    })()}
                  </React.Fragment>
                );
              })}
              {pagedFindings.length === 0 && (
                <tr><td colSpan={7} className="text-center text-muted py-4">{t('vigilScan.noScans')}</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredFindings.length > pageSize && (
          <div className="mt-3">
            <Pagination
              nPages={totalPages}
              currentPage={page}
              setCurrentPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalItems={filteredFindings.length}
            />
          </div>
        )}
        </motion.div>
      </div>

      {/* ── Row 5: scan metadata ───────────────────────────────────────────── */}
      <motion.div className="vigil-card" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <h5 className="fw-semibold mb-3">{t('vigilScan.scanMetadata')}</h5>
        <div className="vigil-meta-grid">
          <div><span className="text-muted">Scan ID</span><br /><code>{scanId}</code></div>
          <div><span className="text-muted">{t('vigilScan.duration')}</span><br />{formatDuration(scanResult?.duration)}</div>
          <div><span className="text-muted">{t('vigilScan.repoPath')}</span><br /><code>{scanResult?.path || scanResult?.repoPath || repoPath}</code></div>
          <div><span className="text-muted">{t('vigilScan.rulesLoaded')}</span><br />{scanResult?.rulesLoaded ?? '—'}</div>
          <div><span className="text-muted">{t('vigilScan.rulesCompiled')}</span><br />{scanResult?.rulesCompiled ?? '—'}</div>
          <div><span className="text-muted">{t('vigilScan.selectPacks')}</span><br />{(scanResult?.rulePacks || selectedPacks || []).join(', ') || '—'}</div>
        </div>
        <div className="mt-3">
          <button className="btn vigil-btn-start" onClick={rescan}>
            🔄 {t('vigilScan.rescan')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
